/**
 * Display helpers shared across the insurance cards.
 *
 * Decimal fields arrive from the backend as strings (Pydantic Decimal serializes
 * to string over JSON). Always parse defensively — backend may also send numbers
 * if the row was set from an integer literal.
 */

const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const INR_LAKHS = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 2,
});

export function toNum(v: string | number | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

/** "₹1,00,00,000" — full currency formatting for headline figures. */
export function formatINR(v: string | number | null | undefined): string {
  const n = toNum(v);
  return n == null ? '—' : INR.format(n);
}

/** "1 Cr" / "50 L" — compact, for cards where space is tight. */
export function formatINRCompact(v: string | number | null | undefined): string {
  const n = toNum(v);
  if (n == null) return '—';
  if (n >= 1e7) return `₹${INR_LAKHS.format(n / 1e7)} Cr`;
  if (n >= 1e5) return `₹${INR_LAKHS.format(n / 1e5)} L`;
  if (n >= 1e3) return `₹${INR_LAKHS.format(n / 1e3)} K`;
  return `₹${n}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function formatFrequency(f: string | null | undefined): string {
  if (!f) return '—';
  const map: Record<string, string> = {
    ANNUAL: 'Annual',
    SEMI_ANNUAL: 'Semi-annual',
    QUARTERLY: 'Quarterly',
    MONTHLY: 'Monthly',
    SINGLE: 'Single premium',
    OTHER: 'Other',
  };
  return map[f] ?? f;
}

export function formatPolicyStatus(
  s: string | null | undefined,
): { label: string; tone: 'success' | 'warning' | 'destructive' | 'muted' } {
  switch (s) {
    case 'ACTIVE':
      return { label: 'Active', tone: 'success' };
    case 'GRACE_PERIOD':
      return { label: 'In grace period', tone: 'warning' };
    case 'LAPSED':
      return { label: 'Lapsed', tone: 'destructive' };
    case 'SURRENDERED':
      return { label: 'Surrendered', tone: 'muted' };
    case 'MATURED':
      return { label: 'Matured', tone: 'muted' };
    default:
      return { label: 'Status not stated', tone: 'muted' };
  }
}

export function formatVariant(v: string | null | undefined): string {
  if (!v) return '—';
  const map: Record<string, string> = {
    LEVEL: 'Level cover',
    INCREASING: 'Increasing cover',
    DECREASING: 'Decreasing cover',
    WHOLE_LIFE: 'Whole-life',
    RETURN_OF_PREMIUM: 'Return of premium',
    OTHER: 'Other',
  };
  return map[v] ?? v;
}

export function formatCoverType(c: string | null | undefined): string {
  if (!c) return '—';
  const map: Record<string, string> = {
    INDIVIDUAL: 'Individual',
    FAMILY_FLOATER: 'Family floater',
    GROUP: 'Group',
    SENIOR_CITIZEN: 'Senior citizen',
    TOP_UP: 'Top-up',
    SUPER_TOP_UP: 'Super top-up',
    OTHER: 'Other',
  };
  return map[c] ?? c;
}

export function formatDocumentType(t: string): string {
  const map: Record<string, string> = {
    term_insurance: 'Term insurance',
    health_insurance: 'Health insurance',
    ecas: 'ECAS (investments)',
    nps: 'NPS statement',
    housing_loan: 'Home loan',
  };
  return map[t] ?? t.replace(/_/g, ' ');
}

export function formatLoanStatus(
  s: string | null | undefined,
): { label: string; tone: 'success' | 'warning' | 'destructive' | 'muted' } {
  switch (s) {
    case 'ACTIVE':
      return { label: 'Active', tone: 'success' };
    case 'CLOSED':
      return { label: 'Closed', tone: 'muted' };
    case 'IN_PROCESS':
      return { label: 'In process', tone: 'warning' };
    case 'OVERDUE':
      return { label: 'Overdue', tone: 'destructive' };
    case 'PREPAID':
      return { label: 'Prepaid', tone: 'muted' };
    default:
      return { label: 'Status not stated', tone: 'muted' };
  }
}

export function formatInterestType(t: string | null | undefined): string {
  switch (t) {
    case 'FLOATING':
      return 'Floating';
    case 'FIXED':
      return 'Fixed';
    case 'HYBRID':
      return 'Hybrid';
    default:
      return '—';
  }
}

export function formatBenchmark(b: string | null | undefined): string {
  switch (b) {
    case 'REPO':
      return 'Repo-linked (EBLR)';
    case 'MCLR':
      return 'MCLR';
    case 'BASE_RATE':
      return 'Base rate';
    case 'PLR':
      return 'PLR';
    default:
      return '—';
  }
}

export function monthsToYears(m: number | null | undefined): string {
  if (m == null) return '—';
  const y = Math.floor(m / 12);
  const r = m % 12;
  if (y === 0) return `${r} mo`;
  if (r === 0) return `${y} yr${y > 1 ? 's' : ''}`;
  return `${y}y ${r}m`;
}

export function formatPct(v: string | number | null | undefined): string {
  const n = toNum(v);
  return n == null ? '—' : `${n % 1 === 0 ? n : n.toFixed(2)}%`;
}

export function formatNpsAssetClass(c: string | null | undefined): string {
  switch (c) {
    case 'EQUITY':
      return 'Equity (E)';
    case 'CORPORATE_BONDS':
      return 'Corporate bonds (C)';
    case 'GOVT_SECURITIES':
      return 'Govt securities (G)';
    case 'ALTERNATIVE':
      return 'Alternative (A)';
    default:
      return 'Other';
  }
}

export function formatNpsTier(t: string | null | undefined): string {
  switch (t) {
    case 'TIER_I':
      return 'Tier I';
    case 'TIER_II':
      return 'Tier II';
    case 'BOTH':
      return 'Tier I & II';
    default:
      return '—';
  }
}

export function formatNpsStatus(
  s: string | null | undefined,
): { label: string; tone: 'success' | 'warning' | 'destructive' | 'muted' } {
  switch (s) {
    case 'ACTIVE':
      return { label: 'Active', tone: 'success' };
    case 'FROZEN':
      return { label: 'Frozen', tone: 'warning' };
    case 'CLOSED':
      return { label: 'Closed', tone: 'destructive' };
    default:
      return { label: 'Status not stated', tone: 'muted' };
  }
}

/**
 * Returns true when the policy is missing nearly all extracted fields,
 * suggesting either a parse failure or a not-yet-extracted document.
 */
export function isLikelyEmpty(p: {
  insurer_name: string | null;
  policy_number: string | null;
  sum_assured?: string | null;
  sum_insured?: string | null;
}): boolean {
  return !p.insurer_name && !p.policy_number && !p.sum_assured && !p.sum_insured;
}
